import React from 'react';
import SEO from '../components/SEO';

const Privacy: React.FC = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy',
    description: 'TurboBetto privacy policy and data protection information',
    url: 'https://turbobetto.com/privacy',
  };

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Read TurboBetto's privacy policy to understand how we collect, use, and protect your personal information."
        url="/privacy"
        schema={schema}
      />

      <div className="pt-24 pb-20 min-h-screen bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-8">Privacy Policy</h1>
            <p className="text-gray-400 mb-8">Last updated: December 2024</p>

            <div className="space-y-8">
              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
                <p className="text-gray-300 leading-relaxed">
                  TurboBetto.com ("we", "our", or "us") is committed to protecting your privacy.
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your
                  information when you visit our website.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
                <div className="space-y-4 text-gray-300">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Personal Information</h3>
                    <p>We may collect personal information that you voluntarily provide, including:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Name and email address (if you contact us)</li>
                      <li>Any information you provide in correspondence</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Automatically Collected Information</h3>
                    <p>We automatically collect certain information when you visit our website:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>IP address and browser type</li>
                      <li>Pages visited and time spent on pages</li>
                      <li>Referring website addresses</li>
                      <li>Device information</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Analyze website usage and trends</li>
                  <li>Ensure website security and prevent fraud</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">4. Cookies and Tracking Technologies</h2>
                <p className="text-gray-300 leading-relaxed">
                  We use cookies and similar tracking technologies to track activity on our website
                  and store certain information. You can instruct your browser to refuse all cookies
                  or to indicate when a cookie is being sent. For more information, please see our
                  Cookie Policy.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">5. Data Sharing and Disclosure</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We do not sell your personal information. We may share your information only in
                  the following circumstances:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>With service providers who assist in operating our website</li>
                  <li>When required by law or to protect our rights</li>
                  <li>In connection with a business transfer or merger</li>
                </ul>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">6. Your Rights</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Under UK GDPR, you have the right to:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Access your personal data</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your data</li>
                  <li>Request data portability</li>
                </ul>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">7. Data Security</h2>
                <p className="text-gray-300 leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your
                  personal information. However, no method of transmission over the Internet is
                  100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">8. Contact Us</h2>
                <p className="text-gray-300 leading-relaxed">
                  If you have questions about this Privacy Policy, please contact us at:
                  <br />
                  <a href="mailto:privacy@turbobetto.com" className="text-yellow-400 hover:underline">
                    privacy@turbobetto.com
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;

